import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { CriterionChooser } from '@/components/admin/protocoles/step/criterion-chooser';
import { CriterionRequirementRow } from '@/components/admin/protocoles/step/criterion-requirement-row';
import { DirectionField } from '@/components/admin/protocoles/step/direction-field';
import { OrGroupFrame } from '@/components/admin/protocoles/step/or-group-frame';
import { OrJoint } from '@/components/admin/protocoles/step/or-joint';
import { DuplicateButton } from '@/components/ui/duplicate-button';
import { Tooltip } from '@/components/ui/tooltip';
import { countLabel, objectiveTitle, obligationLabel } from '@/lib/protocol-labels';
import {
  addCriterion,
  addCriterionToGroup,
  linkCriteria,
  removeCriterion,
  replaceRequirement,
  setQuantity,
  unlinkCriterion,
} from '@/lib/protocol-step-draft';
import { useFocusOnce } from '@/lib/use-focus-once';
import type {
  CriterionCard,
  CriterionGroup,
  CriterionRequirement,
  ProtocolObjectiveDraft,
  ProtocolStepDraft,
} from '@matter_extractor/shared';
import {
  PROTOCOL_OBJECTIVE_NAME_MAX,
  objectiveCriterionGroups,
  orGroupProblems,
} from '@matter_extractor/shared';
import { ChevronRight, GripVertical, Plus, TriangleAlert } from 'lucide-react';
import type { DragEvent as ReactDragEvent, ReactNode } from 'react';
import { useId, useRef, useState } from 'react';

/**
 * One objective, folded to a line and unfolded to a form (refonte modulaire
 * §7.2; maquette `objectiveHTML`).
 *
 * The line says the three things an operator scans for - obligation, name, what
 * validates it - and the body holds everything else. ONE row is open at a time,
 * and the list above holds which: two open bodies would put two four-thousand
 * character directions on one screen and make the list stop being a list.
 *
 * ## The summary is the whole of the validation, or the refusal
 *
 * « 2 critères · ≥ 3 phrases » is what §7.2 asks a row to say. An objective with
 * NO criterion says so in yellow instead, because that is a refusal of « Enregistrer
 * une nouvelle version » (§7.1) and the fold would otherwise hide it: the yellow
 * is on the closed line, not only inside the body.
 *
 * ## The criteria are groups, and a group is « l'un de »
 *
 * The list of citations is flat, and neighbouring ones sharing an `orGroup` are
 * one « l'un de » group (issue #181; `objectiveCriterionGroups` is the one place
 * they are cut). A lone criterion is drawn as before; a group is a titled frame
 * with an « ou » between its lines. The gestures live on the JUNCTION of two
 * lines - « + ou » links them, « ou × » cuts the group there - and the draft's
 * pure functions do the rewriting, so this file only says where a press lands.
 *
 * ## The quantity remembers what was turned off
 *
 * `minSentences` is a number or null and nothing else, so unchecking the box
 * would lose the number typed beside it. The last value is kept in the row's own
 * state and offered back when the box is checked again - the régime the persona
 * form holds for the prénom of an anonymous presentation, and for the same
 * reason: an operator who unchecks a box has not asked to forget.
 */

export interface ObjectiveRowProps {
  objective: ProtocolObjectiveDraft;
  index: number;
  open: boolean;
  /** The whole draft: the palette of the direction needs the other steps. */
  steps: readonly ProtocolStepDraft[];
  stepId: string;
  criteria: readonly CriterionCard[];
  criteriaRead: boolean;
  readOnly: boolean;
  /**
   * True when the step hides its objectives from the interviewer (issue #191,
   * H20): the direction of an objective is then read by the scribe alone, and
   * the pill beside its label says so.
   */
  directionForScribe: boolean;
  /**
   * True when this row is the COPY a duplication just made (§4.2): its name
   * field takes the focus and is selected. The list is what knows it, because
   * the list is what opened this row.
   */
  focusName: boolean;
  /** Said once that focus has been taken, so the list can forget the request. */
  onNameFocused: () => void;
  onToggle: () => void;
  onChange: (next: ProtocolObjectiveDraft) => void;
  onDelete: () => void;
  /** « Dupliquer » - the gutter's copy icon, closed at the cap by `blocked`. */
  onDuplicate: () => void;
  /** Why the copy icon is closed, or `null`. Computed once by the list. */
  duplicationBlocked: string | null;
  /** Where a drop would land this row, as the list computes it. */
  dropHint: 'before' | 'after' | null;
  dragging: boolean;
  onDragStart: () => void;
  onDragOver: (event: ReactDragEvent<HTMLElement>) => void;
  onDrop: (event: ReactDragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
}

const FIELD_BOX =
  'rounded-md border-2 border-gray-300 bg-white read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const LABEL = 'block font-semibold text-gray-800 text-sm';

const DEFAULT_SENTENCES = 3;
const DEFAULT_CHARS = 200;

export function ObjectiveRow({
  objective,
  index,
  open,
  steps,
  stepId,
  criteria,
  criteriaRead,
  readOnly,
  directionForScribe,
  focusName,
  onNameFocused,
  onToggle,
  onChange,
  onDelete,
  onDuplicate,
  duplicationBlocked,
  dropHint,
  dragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: ObjectiveRowProps) {
  const nameId = useId();
  const nameRef = useRef<HTMLInputElement | null>(null);
  const [chooser, setChooser] = useState(false);
  /* Le panneau se place sur ce bouton, et n'est donc plus rogné par la liste. */
  const [addEl, setAddEl] = useState<HTMLButtonElement | null>(null);
  const [lastSentences, setLastSentences] = useState(
    objective.quantity.minSentences ?? DEFAULT_SENTENCES,
  );
  const [lastChars, setLastChars] = useState(objective.quantity.minChars ?? DEFAULT_CHARS);

  // The COPY's name field takes the focus and is selected (§4.2). The hook
  // holds the « once, and then forget » of it - see `lib/use-focus-once.ts`.
  useFocusOnce(focusName && open ? objective.id : null, nameRef, onNameFocused);

  const cardOf = (criterionId: string): CriterionCard | null =>
    criteria.find((one) => one.id === criterionId) ?? null;
  const nameOf = (criterionId: string): string => cardOf(criterionId)?.name ?? criterionId;

  const groups = objectiveCriterionGroups(objective);
  const groupsRefused = orGroupProblems(objective).length > 0;
  const citedIds = objective.criteria.map((one) => one.ref.id);

  const renderRow = (requirement: CriterionRequirement, at: number, grouped: boolean) => (
    <CriterionRequirementRow
      key={`${requirement.ref.id}-${at}`}
      requirement={requirement}
      card={cardOf(requirement.ref.id)}
      criteriaRead={criteriaRead}
      readOnly={readOnly}
      grouped={grouped}
      onChange={(next) => onChange(replaceRequirement(objective, at, next))}
      onRemove={() => onChange(removeCriterion(objective, at))}
    />
  );

  const renderFrame = (
    group: CriterionGroup<CriterionRequirement>,
    orGroup: string,
    start: number,
  ): ReactNode => (
    <OrGroupFrame
      orGroup={orGroup}
      refused={group.criteria.length < 2}
      readOnly={readOnly}
      criteria={criteria}
      citedIds={citedIds}
      onAdd={(criterionId) => onChange(addCriterionToGroup(objective, criterionId, orGroup))}
      onRelease={() => onChange(unlinkCriterion(objective, start))}
    >
      {group.criteria.map((requirement, offset) => {
        const above = group.criteria[offset - 1];
        return (
          <div key={`${requirement.ref.id}-${start + offset}`}>
            {above === undefined ? null : (
              <OrJoint
                kind="unlink"
                upperName={nameOf(above.ref.id)}
                lowerName={nameOf(requirement.ref.id)}
                readOnly={readOnly}
                onPress={() => onChange(unlinkCriterion(objective, start + offset))}
              />
            )}
            {renderRow(requirement, start + offset, true)}
          </div>
        );
      })}
    </OrGroupFrame>
  );

  /*
    One item per group: a lone criterion's row, or the frame of a « l'un de ».
    Each item is positioned, because the « + ou » of the junction below it is
    absolute and straddles the gap to the next item without taking any room.
  */
  let cursor = 0;
  const items: ReactNode[] = groups.map((group, groupAt) => {
    const start = cursor;
    cursor += group.criteria.length;
    const last = group.criteria[group.criteria.length - 1];
    const next = groups[groupAt + 1]?.criteria[0];
    return (
      <div key={`group-${start}`} className="relative">
        {group.either && group.orGroup !== null
          ? renderFrame(group, group.orGroup, start)
          : group.criteria.map((requirement, offset) =>
              renderRow(requirement, start + offset, false),
            )}
        {last === undefined || next === undefined ? null : (
          <OrJoint
            kind="link"
            upperName={nameOf(last.ref.id)}
            lowerName={nameOf(next.ref.id)}
            readOnly={readOnly}
            onPress={() => onChange(linkCriteria(objective, start + group.criteria.length - 1))}
          />
        )}
      </div>
    );
  });

  const dropClass =
    dropHint === 'before'
      ? 'shadow-[inset_0_3px_0_var(--color-bleu-nuit)]'
      : dropHint === 'after'
        ? 'shadow-[inset_0_-3px_0_var(--color-bleu-nuit)]'
        : '';

  return (
    <div className="border-gray-100 border-t first:border-t-0">
      {/*
        The row, then its gutter. The copy icon cannot live INSIDE the header:
        that header is a <button>, and a button may not contain another one -
        the same constraint as the trash of `step-card.tsx`, answered here by a
        column BESIDE it rather than by a `role="button"`, which would cost the
        native behaviour of the disclosure.

        The hover and the drop of a drag therefore sit on the wrapper, so the
        gutter is a drop target like the rest of the row; its height is the
        header's alone, so the midpoint the list measures does not move when the
        body is unfolded.
      */}
      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={`flex items-center ${dragging ? 'opacity-40' : ''} ${dropClass}`}
      >
        <button
          type="button"
          data-testid={PROTOCOL_TEST_IDS.objective}
          data-objective-id={objective.id}
          aria-expanded={open}
          draggable={!readOnly}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onClick={onToggle}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {readOnly ? null : (
            // The bubble hangs on the `<span>` and not on the icon: the `<span>`
            // is the hit area the pointer meets, and it is what takes the ref and
            // the handlers the bubble needs.
            <Tooltip label="Glisser pour réordonner">
              <span className="flex shrink-0 cursor-grab">
                <GripVertical aria-hidden="true" className="size-4 text-gray-300" />
              </span>
            </Tooltip>
          )}
          <span
            className={`inline-flex shrink-0 items-center rounded-pill px-2.5 py-0.5 font-semibold text-xs leading-4 ${
              objective.mandatory ? 'bg-bleu-nuit-tint text-bleu-nuit' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {obligationLabel(objective.mandatory)}
          </span>
          <span className="min-w-0 truncate font-semibold text-base">
            {objectiveTitle(objective.name, index)}
          </span>
          <span className="ml-auto shrink-0 whitespace-nowrap text-caption text-gray-600">
            {objective.criteria.length === 0 ? (
              <span className="inline-flex items-center gap-1.5 text-jaune-deep">
                <TriangleAlert aria-hidden="true" className="size-3.5" />
                aucun critère
              </span>
            ) : groupsRefused ? (
              <span className="inline-flex items-center gap-1.5 text-jaune-deep">
                <TriangleAlert aria-hidden="true" className="size-3.5" />« l’un de » d’un seul
                critère
              </span>
            ) : (
              summaryOf(objective)
            )}
          </span>
          <ChevronRight
            aria-hidden="true"
            className={`size-4 shrink-0 text-gray-500 ${open ? 'rotate-90' : ''}`}
          />
        </button>
        {readOnly ? null : (
          <DuplicateButton
            testId={PROTOCOL_TEST_IDS.duplicateObjective}
            label={`Dupliquer l’objectif ${index + 1}`}
            blocked={duplicationBlocked}
            shape="compact"
            className="mr-2"
            onClick={onDuplicate}
          />
        )}
      </div>

      {open ? (
        <div className="grid gap-4 border-gray-100 border-t bg-gray-50 py-3 pr-3 pl-11">
          <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
            <div className="space-y-1.5">
              <label htmlFor={nameId} className={LABEL}>
                Nom
              </label>
              <input
                id={nameId}
                ref={nameRef}
                data-testid={PROTOCOL_TEST_IDS.objectiveName}
                value={objective.name}
                readOnly={readOnly}
                maxLength={PROTOCOL_OBJECTIVE_NAME_MAX}
                onChange={(event) => onChange({ ...objective, name: event.currentTarget.value })}
                className={`${FIELD_BOX} h-11 w-full px-3 text-base`}
              />
            </div>
            <div className="space-y-1.5">
              <p className={LABEL}>Obligation</p>
              <div className="inline-flex overflow-hidden rounded-md border-2 border-gray-300">
                <ObligationButton
                  testId={PROTOCOL_TEST_IDS.objectiveMandatory}
                  label="obligatoire"
                  divider={false}
                  pressed={objective.mandatory}
                  readOnly={readOnly}
                  onPress={() => onChange({ ...objective, mandatory: true })}
                />
                <ObligationButton
                  testId={PROTOCOL_TEST_IDS.objectiveOptional}
                  label="optionnel"
                  divider
                  pressed={!objective.mandatory}
                  readOnly={readOnly}
                  onPress={() => onChange({ ...objective, mandatory: false })}
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <p className={LABEL}>Direction</p>
              <span
                data-testid={PROTOCOL_TEST_IDS.objectiveDirectionReader}
                className="inline-flex items-center rounded-pill bg-gray-100 px-2 py-0.5 font-semibold text-gray-700 text-micro"
              >
                {directionForScribe ? 'le scribe' : 'l’interviewer'}
              </span>
            </div>
            <DirectionField
              value={objective.direction}
              onChange={(next) => onChange({ ...objective, direction: next })}
              readOnly={readOnly}
              placeholder="Ce que l’interviewer cherche à obtenir pour cet objectif."
              ariaLabel={`Direction de l’objectif ${objectiveTitle(objective.name, index)}`}
              testId={PROTOCOL_TEST_IDS.objectiveDirection}
              insertTestId={null}
              steps={steps}
              stepId={stepId}
              criteria={criteria}
              minHeightClass="min-h-20"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className={LABEL}>Condition de validation</p>
              <span className="text-gray-600 text-xs">
                chaque ligne est requise ; dans un « l’un de », un seul critère suffit
              </span>
            </div>
            <div className="flex flex-col gap-1">
              {items}
              {objective.criteria.length === 0 ? (
                <p className="inline-flex items-center gap-1.5 text-jaune-deep text-xs">
                  <TriangleAlert aria-hidden="true" className="size-3.5" />
                  sans critère, l’enregistrement d’une version refusera cet objectif
                </p>
              ) : null}
              {groupsRefused ? (
                <p
                  data-testid={PROTOCOL_TEST_IDS.criterionGroupRefusal}
                  className="inline-flex items-center gap-1.5 text-jaune-deep text-xs"
                >
                  <TriangleAlert aria-hidden="true" className="size-3.5" />
                  Un groupe « l’un de » demande au moins deux critères.
                </p>
              ) : null}
              {readOnly ? null : (
                <div>
                  <button
                    ref={setAddEl}
                    type="button"
                    data-testid={PROTOCOL_TEST_IDS.addCriterion}
                    aria-expanded={chooser}
                    onClick={() => setChooser((was) => !was)}
                    className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2.5 font-semibold text-bleu-nuit text-caption hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Plus aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
                    Ajouter un critère
                  </button>
                  {chooser ? (
                    <CriterionChooser
                      criteria={criteria}
                      citedIds={citedIds}
                      anchor={addEl}
                      onClose={() => setChooser(false)}
                      onPick={(criterionId) => {
                        setChooser(false);
                        onChange(addCriterion(objective, criterionId));
                      }}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <p className={LABEL}>
              Quantité de matière attendue{' '}
              <span className="font-normal text-gray-600">· facultatif</span>
            </p>
            <div className="flex flex-wrap gap-5 text-sm">
              <QuantityLine
                testId={PROTOCOL_TEST_IDS.quantitySentences}
                label="phrases"
                fieldLabel="Nombre minimum de phrases"
                value={objective.quantity.minSentences}
                fallback={lastSentences}
                readOnly={readOnly}
                onToggle={(on) =>
                  onChange(
                    setQuantity(objective, {
                      ...objective.quantity,
                      minSentences: on ? lastSentences : null,
                    }),
                  )
                }
                onValue={(next) => {
                  setLastSentences(next);
                  onChange(setQuantity(objective, { ...objective.quantity, minSentences: next }));
                }}
              />
              <QuantityLine
                testId={PROTOCOL_TEST_IDS.quantityChars}
                label="caractères"
                fieldLabel="Nombre minimum de caractères"
                value={objective.quantity.minChars}
                fallback={lastChars}
                readOnly={readOnly}
                onToggle={(on) =>
                  onChange(
                    setQuantity(objective, {
                      ...objective.quantity,
                      minChars: on ? lastChars : null,
                    }),
                  )
                }
                onValue={(next) => {
                  setLastChars(next);
                  onChange(setQuantity(objective, { ...objective.quantity, minChars: next }));
                }}
              />
            </div>
          </div>

          {readOnly ? null : (
            <div className="flex justify-end">
              <button
                type="button"
                data-testid={PROTOCOL_TEST_IDS.deleteObjective}
                onClick={onDelete}
                className="cursor-pointer text-caption text-rouge-deep underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Supprimer l’objectif
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** « 2 critères · ≥ 3 phrases · ≥ 200 car. » - what a folded row says (§7.2). */
function summaryOf(objective: ProtocolObjectiveDraft): string {
  const parts = [countLabel(objective.criteria.length, 'critère')];
  const eithers = objectiveCriterionGroups(objective).filter((group) => group.either).length;
  if (eithers > 0) parts.push(countLabel(eithers, '« l’un de »', '« l’un de »'));
  if (objective.quantity.minSentences !== null) {
    parts.push(`≥ ${objective.quantity.minSentences} phrases`);
  }
  if (objective.quantity.minChars !== null) parts.push(`≥ ${objective.quantity.minChars} car.`);
  return parts.join(' · ');
}

function ObligationButton({
  testId,
  label,
  divider,
  pressed,
  readOnly,
  onPress,
}: {
  testId: string;
  label: string;
  /** The 2 px rule between the two halves - the second button carries it. */
  divider: boolean;
  pressed: boolean;
  readOnly: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={pressed}
      disabled={readOnly}
      onClick={onPress}
      className={`h-9 cursor-pointer px-3.5 text-sm disabled:cursor-default ${divider ? 'border-gray-300 border-l-2' : ''} ${
        pressed ? 'bg-bleu-nuit font-semibold text-white' : 'bg-white text-gray-700'
      }`}
    >
      {label}
    </button>
  );
}

/** « au moins N phrases », the checkbox and the number in one label (§7.2). */
function QuantityLine({
  testId,
  label,
  fieldLabel,
  value,
  fallback,
  readOnly,
  onToggle,
  onValue,
}: {
  testId: string;
  label: string;
  /** The number field's accessible name - the visible words are split in two. */
  fieldLabel: string;
  value: number | null;
  /** What checking the box comes back to: the last number typed here. */
  fallback: number;
  readOnly: boolean;
  onToggle: (on: boolean) => void;
  onValue: (next: number) => void;
}) {
  const on = value !== null;
  return (
    <label
      className={`inline-flex items-center gap-2 ${on ? 'text-gray-800' : 'text-gray-500'} ${readOnly ? '' : 'cursor-pointer'}`}
    >
      <input
        type="checkbox"
        checked={on}
        disabled={readOnly}
        onChange={(event) => onToggle(event.currentTarget.checked)}
        className="size-4 accent-bleu-nuit"
      />
      au moins
      <input
        type="number"
        min={1}
        data-testid={testId}
        aria-label={fieldLabel}
        value={value ?? fallback}
        readOnly={readOnly}
        disabled={!on}
        onChange={(event) => {
          const next = Number(event.currentTarget.value);
          onValue(Number.isFinite(next) ? Math.max(1, Math.trunc(next)) : 1);
        }}
        className="h-8 w-16 rounded-md border-2 border-gray-300 bg-white px-1.5 text-right text-sm read-only:bg-gray-50 disabled:bg-gray-50 disabled:text-gray-400"
      />
      {label}
    </label>
  );
}
